import type { Session } from '@/api/client';
import {
  parseTimeToMinutes,
  buildTimeFromMinutes,
  getStudioIndex,
  getMaxDurationAtPosition,
} from '@/schedule/scheduleUtils';
import { TOTAL_MINUTES } from '@/schedule/scheduleConstants';

const SLOT_STEP = 30;
const MIN_DURATION = 30;

function timeRangesOverlap(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 < b1 && a1 > b0;
}

/** Активные сессии дня (не отменённые), без восстанавливаемой */
function blockingSessionsForRestore(all: Session[], excludeId: string): Session[] {
  return all.filter((s) => s.id !== excludeId && !(s.is_canceled ?? false));
}

function fitsInterval(
  blocking: Session[],
  date: string,
  studioIdx: number,
  startMins: number,
  endMins: number
): boolean {
  const ds = date.slice(0, 10);
  for (const s of blocking) {
    if ((s.date as string).slice(0, 10) !== ds) continue;
    if (getStudioIndex(s.studio) !== studioIdx) continue;
    const ss = parseTimeToMinutes(s.start as string);
    const se = parseTimeToMinutes(s.end as string);
    if (timeRangesOverlap(startMins, endMins, ss, se)) return false;
  }
  return true;
}

export interface RestoredPlacement {
  date: string;
  start: string;
  end: string;
  studio: string;
}

type BestSlot = { studioIdx: number; startMins: number; dur: number };

function betterSlot(a: BestSlot, b: BestSlot, origStudioIdx: number): BestSlot {
  if (a.dur !== b.dur) return a.dur > b.dur ? a : b;
  const aOrig = a.studioIdx === origStudioIdx ? 0 : 1;
  const bOrig = b.studioIdx === origStudioIdx ? 0 : 1;
  if (aOrig !== bOrig) return aOrig < bOrig ? a : b;
  if (a.startMins !== b.startMins) return a.startMins < b.startMins ? a : b;
  return a.studioIdx <= b.studioIdx ? a : b;
}

/**
 * Восстановление на исходный слот при возможности; иначе ближайшее свободное время в той же студии,
 * затем другие студии, затем укороченная длительность.
 */
export function computeRestoredSessionPlacement(session: Session, allSessions: Session[]): RestoredPlacement {
  const date = (session.date as string).slice(0, 10);
  const origStart = parseTimeToMinutes(session.start as string);
  const origEnd = parseTimeToMinutes(session.end as string);
  const origDur = Math.max(MIN_DURATION, origEnd - origStart);
  const origStudioIdx = getStudioIndex(session.studio);
  const blocking = blockingSessionsForRestore(allSessions, session.id);

  if (origStart + origDur <= TOTAL_MINUTES && fitsInterval(blocking, date, origStudioIdx, origStart, origStart + origDur)) {
    return {
      date,
      start: buildTimeFromMinutes(origStart),
      end: buildTimeFromMinutes(origStart + origDur),
      studio: String(origStudioIdx + 1),
    };
  }

  const studioOrder = [origStudioIdx, ...[0, 1, 2, 3, 4, 5].filter((i) => i !== origStudioIdx)];

  for (const studioIdx of studioOrder) {
    const startCandidates: number[] = [];
    if (studioIdx === origStudioIdx) {
      for (let t = origStart; t + MIN_DURATION <= TOTAL_MINUTES; t += SLOT_STEP) {
        startCandidates.push(t);
      }
    } else {
      for (let t = 0; t + MIN_DURATION <= TOTAL_MINUTES; t += SLOT_STEP) {
        startCandidates.push(t);
      }
    }
    for (const startMins of startCandidates) {
      if (startMins + origDur > TOTAL_MINUTES) continue;
      if (fitsInterval(blocking, date, studioIdx, startMins, startMins + origDur)) {
        return {
          date,
          start: buildTimeFromMinutes(startMins),
          end: buildTimeFromMinutes(startMins + origDur),
          studio: String(studioIdx + 1),
        };
      }
    }
  }

  let best: BestSlot | null = null;
  for (let studioIdx = 0; studioIdx < 6; studioIdx++) {
    for (let t = 0; t + MIN_DURATION <= TOTAL_MINUTES; t += SLOT_STEP) {
      const maxDur = getMaxDurationAtPosition(allSessions, date, studioIdx, t, session.id);
      if (maxDur < MIN_DURATION) continue;
      const dur = Math.min(origDur, Math.floor(maxDur / SLOT_STEP) * SLOT_STEP);
      if (dur < MIN_DURATION) continue;
      const cand: BestSlot = { studioIdx, startMins: t, dur };
      best = best ? betterSlot(best, cand, origStudioIdx) : cand;
    }
  }

  if (!best) {
    const t = Math.max(0, TOTAL_MINUTES - MIN_DURATION);
    return {
      date,
      start: buildTimeFromMinutes(t),
      end: buildTimeFromMinutes(TOTAL_MINUTES),
      studio: '1',
    };
  }

  return {
    date,
    start: buildTimeFromMinutes(best.startMins),
    end: buildTimeFromMinutes(best.startMins + best.dur),
    studio: String(best.studioIdx + 1),
  };
}
